import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, already_verified
  const [message, setMessage] = useState('Verifying your email...');
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${backendUrl}/api/auth/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (response.ok) {
          if (data.already_verified) {
            setStatus('already_verified');
            setMessage('Your email is already verified!');
          } else {
            setStatus('success');
            setMessage('Email verified successfully!');
          }
          
          // Refresh user data to update verification status across app
          // This will trigger TierContext to refetch user data
          const userDataRefreshEvent = new Event('user-data-changed');
          window.dispatchEvent(userDataRefreshEvent);
          
          // Redirect to dashboard after 3 seconds
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        } else {
          setStatus('error');
          setMessage(data.detail || 'Verification failed. Please try again.');
        }
      } catch (error) {
        setStatus('error');
        setMessage('Network error. Please check your connection and try again.');
        console.error('Verification error:', error);
      }
    };

    verifyEmail();
  }, [token, navigate]);

  const handleResendVerification = () => {
    navigate('/dashboard');
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        maxWidth: '500px',
        width: '100%',
        padding: '40px',
        textAlign: 'center'
      }}>
        {/* Status Icon */}
        <div style={{ marginBottom: '20px' }}>
          {status === 'verifying' && (
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              margin: '0 auto',
              animation: 'spin 1s linear infinite'
            }} />
          )}
          
          {status === 'success' && (
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              fontSize: '30px',
              color: 'white'
            }}>
              ✓
            </div>
          )}
          
          {status === 'already_verified' && (
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              fontSize: '30px',
              color: 'white'
            }}>
              ✓
            </div>
          )}
          
          {status === 'error' && (
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              fontSize: '30px',
              color: 'white'
            }}>
              ✕
            </div>
          )}
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#1f2937',
          marginBottom: '12px'
        }}>
          {status === 'verifying' && 'Verifying Email'}
          {status === 'success' && 'Email Verified!'}
          {status === 'already_verified' && 'Already Verified'}
          {status === 'error' && 'Verification Failed'}
        </h1>

        {/* Message */}
        <p style={{
          fontSize: '16px',
          color: '#6b7280',
          marginBottom: '30px',
          lineHeight: '1.6'
        }}>
          {message}
        </p>

        {/* Actions */}
        {status === 'success' && (
          <p style={{
            fontSize: '14px',
            color: '#9ca3af',
            fontStyle: 'italic'
          }}>
            Redirecting to dashboard in 3 seconds...
          </p>
        )}

        {status === 'already_verified' && (
          <p style={{
            fontSize: '14px',
            color: '#9ca3af',
            fontStyle: 'italic'
          }}>
            Redirecting to dashboard in 3 seconds...
          </p>
        )}

        {status === 'error' && (
          <div>
            <button
              onClick={handleResendVerification}
              style={{
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '12px 30px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginBottom: '15px'
              }}
              onMouseOver={(e) => e.target.style.background = '#5568d3'}
              onMouseOut={(e) => e.target.style.background = '#667eea'}
            >
              Request New Verification Link
            </button>
            
            <p style={{
              fontSize: '14px',
              color: '#9ca3af'
            }}>
              or <a href="/#/login" style={{ color: '#667eea', textDecoration: 'none' }}>return to login</a>
            </p>
          </div>
        )}
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default VerifyEmail;
